import React, { useState, useRef } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Box, Button, CircularProgress, Alert } from "@mui/material";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import {
  canUseGoogleAds,
  ensureGoogleTagLoaded,
  reportGoogleAdsConversion,
} from "../utils/googleAds";
import { useLang } from "../context/LangContext";
import locales from "../locales.json";

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollUntilActive(
  fetchUser,
  { interval = 4000, timeout = 120000 } = {},
) {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const user = await fetchUser();
    if (user?.status === "active") return user;
    await delay(interval);
  }
  throw new Error("Activation trop longue. Veuillez réessayer plus tard.");
}

const CheckoutForm = ({ onClose, frequency, licenseType, userCount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { lang } = useLang();
  const { payment_page } = locales;

  const [phase, setPhase] = useState("idle"); // idle | creating | confirming | pending | active | error
  const [errorMessage, setErrorMessage] = useState("");
  const [activationMessage, setActivationMessage] = useState("");
  const conversionFiredRef = useRef(false);

  // Simule un fetchUser (à adapter selon ton API)
  const fetchUser = async () => {
    const res = await fetchWithAuth("users/me", { method: "GET" });
    return res.ok ? await res.json() : null;
  };

  const resolveTransactionId = (payload) =>
    payload?.transactionId ||
    payload?.paymentIntentId ||
    payload?.payment_intent_id ||
    payload?.invoiceId ||
    payload?.invoice_id ||
    payload?.subscriptionId ||
    payload?.subscription_id ||
    payload?.chargeId ||
    payload?.charge_id ||
    "";

  const fireConversionOnce = (transactionId) => {
    if (conversionFiredRef.current) {
      return;
    }

    if (!canUseGoogleAds()) {
      return;
    }

    conversionFiredRef.current = true;
    if (!ensureGoogleTagLoaded()) {
      return;
    }
    if (!reportGoogleAdsConversion({ transactionId })) {
      setTimeout(() => {
        reportGoogleAdsConversion({ transactionId });
      }, 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setPhase("creating");

    if (!stripe || !elements) {
      setErrorMessage("Stripe n'est pas prêt.");
      setPhase("error");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setErrorMessage("Champ carte introuvable.");
      setPhase("error");
      return;
    }

    // Crée un PaymentMethod Stripe
    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (pmError) {
      setErrorMessage(pmError.message);
      setPhase("error");
      return;
    }

    // Appelle le backend pour créer la souscription
    let data;
    try {
      const res = await fetchWithAuth(
        "/subscriptions/create-subscription-credit-card",
        {
          method: "POST",
          body: JSON.stringify({
            paymentMethodId: paymentMethod.id,
            frequency,
            licenseType,
            userCount,
          }),
        },
      );
      data = await res.json();
      if (!res.ok)
        throw new Error(
          data.error || "Erreur lors de la création de la souscription.",
        );
    } catch (err) {
      setErrorMessage(err.message);
      setPhase("error");
      return;
    }

    // Gestion SCA/3DS
    if (data.requiresAction && data.clientSecret) {
      setPhase("confirming");
      const result = await stripe.confirmCardPayment(data.clientSecret);
      if (result.error) {
        setErrorMessage(result.error.message);
        setPhase("error");
        return;
      }
      // Paiement réussi, attendre activation
      setPhase("pending");
      setActivationMessage(
        payment_page.activation_pending[lang] || "Activation en cours...",
      );
      try {
        await delay(1500);
        await pollUntilActive(fetchUser, { interval: 4000, timeout: 120000 });
        setPhase("active");
        fireConversionOnce(resolveTransactionId(data));
      } catch (err) {
        setErrorMessage(err.message);
        setPhase("error");
      }
      return;
    }

    // Si pas d'action requise mais userStatus = pending, poll jusqu'à activation
    if (data.userStatus === "pending") {
      setPhase("pending");
      setActivationMessage(
        payment_page.activation_pending[lang] || "Activation en cours...",
      );
      try {
        await pollUntilActive(fetchUser, { interval: 4000, timeout: 120000 });
        setPhase("active");
        fireConversionOnce(resolveTransactionId(data));
      } catch (err) {
        setErrorMessage(err.message);
        setPhase("error");
      }
      return;
    }

    // Si userStatus = active directement
    if (data.userStatus === "active") {
      setPhase("active");
      fireConversionOnce(resolveTransactionId(data));
      return;
    }

    // Compat: backend renvoie stripeStatus au lieu de userStatus
    if (data.stripeStatus === "active" || data.status === "active") {
      setPhase("active");
      fireConversionOnce(resolveTransactionId(data));
      return;
    }

    // Si paymentIntentStatus = requires_payment_method (échec carte)
    if (data.paymentIntentStatus === "requires_payment_method") {
      setErrorMessage(
        payment_page.card_failed[lang] || "Carte refusée. Veuillez réessayer.",
      );
      setPhase("error");
      return;
    }

    // Cas inattendu
    setErrorMessage(payment_page.unknown_error[lang] || "Erreur inconnue.");
    setPhase("error");
  };

  // UI selon phase
  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ my: 2 }}>
        <CardElement options={{ hidePostalCode: true }} />
      </Box>
      {phase === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {phase === "pending" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {activationMessage}
        </Alert>
      )}
      {phase === "active" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {payment_page.activation_success[lang] || "Abonnement activé !"}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          onClick={onClose}
          disabled={
            phase === "creating" ||
            phase === "confirming" ||
            phase === "pending"
          }
        >
          {payment_page.cancel[lang]}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={
            phase === "creating" ||
            phase === "confirming" ||
            phase === "pending" ||
            phase === "active"
          }
        >
          {phase === "creating"
            ? payment_page.payment_processing[lang] || "Traitement…"
            : payment_page.pay[lang] || "Payer"}
        </Button>
      </Box>
      {(phase === "creating" ||
        phase === "confirming" ||
        phase === "pending") && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </form>
  );
};

export default CheckoutForm;
